/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useCart from '@/public/hooks/use-cart'
import {
  setOpen,
  setError,
  setLoading,
  applySuggestions,
  switchLabCompany,
  selectOpen,
  selectLoading,
  selectError,
} from '@/public/redux/cartSlice'
import { accessToken } from '@/shared/helpers/CSRF'
import { numberToCurrency } from '@/shared/helpers/String'
import LoadingOverlay from '@/public/LoadingOverlay'
import CartItem from '@/public/cart/CartItem'
import VirtualVisitCartItem from '@/public/cart/VirtualVisitCartItem'
import CartLedger from '@/public/cart/CartLedger'
import MixedLabCompanyBanner from '@/public/cart/MixedLabCompanyBanner'
import LabCompanyNoticeBanner from '@/public/cart/LabCompanyNoticeBanner'

// `parseFloat(undefined)` is NaN, and NaN survives both the arithmetic below and
// numberToCurrency's `toFixed` -- it reaches the DOM as the literal string "$NaN".
// Coercing every operand at the boundary keeps one missing or non-numeric field
// from poisoning the whole total, instead of guarding field by field as each
// becomes a problem.
const toAmount = (value) => {
  const amount = parseFloat(value)
  return Number.isFinite(amount) ? amount : 0
}

export default function CartDrawer({
  checkoutUrl,
  storeDefaultUrl,
  customerIsMember,
  monthlyMembership,
  membershipPlansEnabled = false,
  annualMembership,
  userSponsoredAllowance = null,
  virtualVisits,
}) {
  const { cart, mutate, isError } = useCart()

  const drawer = useRef(null)
  const cartList = useRef(null)

  const dispatch = useDispatch()
  const open = useSelector(selectOpen)
  const loading = useSelector(selectLoading)
  const cartError = useSelector(selectError)
  const fullCheckoutUrl = `${checkoutUrl}/${cart?.uuid}`

  const cartContainsLabOrTest = cart?.contents?.some((cartItem) => cartItem.data.category === 'lab' || cartItem.data.category === 'test')
  const totalAfterSavings = toAmount(cart?.calculated_total_after_savings)
  const sponsoredAllowance = toAmount(cart?.sponsored_allowance)
  const supplementInvoicingAmount = toAmount(cart?.supplement_invoicing_amount)
  const checkoutPrice = totalAfterSavings - sponsoredAllowance - supplementInvoicingAmount

  // There is nothing to check out until the cart both exists and holds a line
  // item. Without a uuid the checkout href is literally `${checkoutUrl}/undefined`,
  // so the control has to be inert rather than merely showing a $0.00 total.
  const checkoutDisabled = !cart?.uuid || !cart?.contents?.length
  const checkoutLabel = (
    <>
      Checkout &mdash;
      {' '}
      {numberToCurrency(checkoutPrice)}
    </>
  )

  const handleClose = () => {
    dispatch(setError(null))
    dispatch(setOpen(false))
  }

  const handleContinueShopping = () => {
    handleClose()
    if (!window.location.href.startsWith(storeDefaultUrl)) {
      window.location.href = storeDefaultUrl
    }
  }

  const handleApplySuggestions = () => {
    if (!cartError?.suggestions || !cart) return

    dispatch(setLoading(true))
    applySuggestions(accessToken, cart, cartError.suggestions, cartError.product, mutate, dispatch)
  }

  const handleSwitchLabCompany = () => {
    const switchInfo = cartError?.switch_lab_company
    if (!switchInfo || !cart) return

    dispatch(setLoading(true))
    switchLabCompany(accessToken, cart, switchInfo, cartError.product, mutate, dispatch)
  }

  const handleClickOutside = (e) => {
    if (
      drawer.current
      && drawer.current.classList.contains('open')
      && drawer.current.contains(e.target) === false
    ) {
      handleClose()
    }
  }

  const preloadCheckout = () => {
    window.__preloader = window.setTimeout(() => {
      if (cart?.uuid && cart.uuid !== '') {
        const prefetch = document.createElement('link')
        prefetch.setAttribute('rel', 'prefetch')
        prefetch.setAttribute('href', fullCheckoutUrl)
        document.head.appendChild(prefetch)

        const prerender = document.createElement('link')
        prefetch.setAttribute('rel', 'prerender')
        prefetch.setAttribute('href', fullCheckoutUrl)
        document.head.appendChild(prerender)
      }
    }, 400)
  }

  const cancelPreloadTimer = () => {
    window.clearTimeout(window.__preloader)
  }

  useEffect(() => {
    if (cartError && cartList.current) {
      cartList.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [cartError])

  /**
   * Mount/Unmount
   */
  useEffect(() => {
    document.addEventListener('click', handleClickOutside, false)

    return () => {
      document.removeEventListener('click', handleClickOutside, false)
    }
  }, [])

  return (
    <div ref={drawer} className={`cart-pane cart-drawer ${open ? 'open' : ''}`} role="dialog" aria-modal="true">
      <LoadingOverlay show={loading} className="tan" />

      <div className="cart-header">
        <div className="h2 underline">Cart</div>
        <button className="btn cart-close p-0" type="button" onClick={handleClose}>
          <i className="icon-times" aria-label="Close" />
        </button>
      </div>

      <div ref={cartList} className="cart-list">
        <LabCompanyNoticeBanner cart={cart} />
        <MixedLabCompanyBanner show={Boolean(cart?.mixed_lab_company)} />

        {cartError && (
          <div className="alert alert-danger mx-3 mb-3" role="alert" aria-live="polite">
            <div className="d-flex align-items-start">
              <div className="flex-grow-1">
                {typeof cartError === 'string' ? cartError : cartError.message}
              </div>
              <button
                type="button"
                className="btn-close ms-2"
                aria-label="Dismiss"
                onClick={() => dispatch(setError(null))}
              />
            </div>
            {cartError?.link && (
              <a href={cartError.link} className="d-block mt-2" target="_blank" rel="noopener noreferrer">
                {cartError.link_text || 'View details'}
              </a>
            )}
            {cartError?.suggestions?.items_to_remove?.length > 0 && (
              <>
                <ul className="mb-2 mt-1 ps-3">
                  {cartError.suggestions.items_to_remove.map((item) => (
                    <li key={item.product_id}>{item.name}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleApplySuggestions}
                >
                  Apply Suggestions
                </button>
              </>
            )}
            {cartError?.switch_lab_company && (
              <div className="d-flex gap-2 mt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleSwitchLabCompany}
                >
                  {cartError.switch_lab_company.incoming_company
                    ? `Switch to ${cartError.switch_lab_company.incoming_company}`
                    : 'Switch lab company'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-link"
                  onClick={() => dispatch(setError(null))}
                >
                  Keep current
                </button>
              </div>
            )}
          </div>
        )}

        {cart?.contents?.map((item, index) => {
          const isVirtualVisit = item.data.category === 'virtual_visit'

          return isVirtualVisit ? (
            <VirtualVisitCartItem
              key={`cart-item-${index}`}
              data={item.data}
              virtualVisit={virtualVisits[item.data.id]}
              expiresAtTimestamp={item.expires_at_timestamp}
              showTimer={index === 0}
            />
          ) : (
            <CartItem
              key={`cart-item-${index}`}
              data={item.data}
              quantity={item.quantity}
              quantifiable={item.quantifiable}
              subscription={item.subscription}
              pickupOrDelivery={item?.pickup_or_delivery}
              customerIsMember={customerIsMember}
              cartContainsLabOrTest={cartContainsLabOrTest}
              monthlyMembership={monthlyMembership}
              membershipPlansEnabled={membershipPlansEnabled}
              annualMembership={annualMembership}
              labCompanyName={item.lab_company_name}
            />
          )
        })}

        {
          isError
            ? <div className="text-danger">There was an error loading your cart.</div>
            : <CartLedger userSponsoredAllowance={userSponsoredAllowance} />
        }
      </div>

      <div className="cart-footer">
        {/* <div className="cart-footer-actions"> */}
        <div className="row">
          <div className="mb-3 mb-md-0 col-sm-6">
            <button type="button" className="btn btn-secondary btn-block" onClick={handleContinueShopping}>
              Continue Shopping
            </button>
          </div>
          {!isError && (
            <div className="mb-3 mb-md-0 col-sm-6">
              {checkoutDisabled ? (
                <button type="button" className="btn btn-primary btn-block" disabled>
                  {checkoutLabel}
                </button>
              ) : (
                <a
                  href={fullCheckoutUrl}
                  className="btn btn-primary btn-block"
                  onMouseEnter={preloadCheckout}
                  onMouseLeave={cancelPreloadTimer}
                >
                  {checkoutLabel}
                </a>
              )}
            </div>
          )}
        </div>

        {/* </div> */}
      </div>
    </div>
  )
}
